import { useEffect, useState } from 'react';
import { Head, router } from '@inertiajs/react';
import {
    Box,
    Typography,
    CardContent,
    CardMedia,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SupportIcon from '@mui/icons-material/Support';
import MainLayout from '../../components/MainLayout';
import { PrimaryButton, SecondaryButton, CustomCard } from '../../components';
import CurrentBillsCard, { type CurrentCycle } from '../../components/CurrentBillsCard';
import ExpectedBillsCard, { type ExpectedBill } from '../../components/ExpectedBillsCard';
import { useAuth } from '../../hooks/useAuth';
import axiosClient from '../../services/axiosClient';
import beautifulBmoreImage from '../../assets/beautiful-bmore-1.jpg';

interface UtilDashboardProps {
    household_id: string;
    household_nickname: string;
    household_email: string;
    property_address: string;
    roomies_count: number;
    current_cycle: CurrentCycle;
    current_user_roommate_id: string;
    user?: { id: string };
    bill_payer_venmo_handle?: string | null;
    is_customer?: boolean;
    current_roommate_is_admin?: boolean;
    expected_bills?: ExpectedBill[];
}

export default function UtilDashboard(props: UtilDashboardProps) {
    const { user } = useAuth();
    const householdNickname = props.household_nickname;
    const roomiesCount = props.roomies_count;
    const currentUserRoommateId = props.current_user_roommate_id;
    const propertyAddress = props.property_address;
    const householdEmail = props.household_email;
    const billPayerVenmoHandle = props.bill_payer_venmo_handle;
    const canManageRoster = Boolean(props.is_customer || props.current_roommate_is_admin);

    const [currentCycle, setCurrentCycle] = useState<CurrentCycle>(props.current_cycle);
    const [expectedBills, setExpectedBills] = useState<ExpectedBill[]>(props.expected_bills ?? []);

    useEffect(() => {
        setCurrentCycle(props.current_cycle);
    }, [props.current_cycle]);

    useEffect(() => {
        setExpectedBills(props.expected_bills ?? []);
    }, [props.expected_bills]);

    // Refresh roster from API after mutations; initial data comes from Inertia props.
    useEffect(() => {
        if (!canManageRoster) return;
        axiosClient.get(`/util/dashboard/${props.household_id}/expected_bills`)
            .then((response) => setExpectedBills(response.data?.expected_bills ?? []))
            .catch((error) => console.error('Failed to load expected bills roster:', error));
    }, [canManageRoster, props.household_id]);

    const [supportOpen, setSupportOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const handleOpenSupport = () => setSupportOpen(true);
    const handleCloseSupport = () => setSupportOpen(false);
    const handleSignOut = () => router.delete('/logout');

    const handleCopyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage(`${label} copied to clipboard!`);
            setSnackbarOpen(true);
        } catch {
            setSnackbarMessage('Failed to copy to clipboard');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => setSnackbarOpen(false);

    const customerId = props.user?.id ?? user?.id ?? '';

    return (
        <>
            <Head title="Utility Dashboard - HeyyProperty" />
            <MainLayout
                headerButton={
                    <SecondaryButton onClick={handleSignOut}>
                        Sign out
                    </SecondaryButton>
                }
                utilDashboard
                householdId={props.household_id}
            >
                {/* First card: Welcome */}
                <CustomCard>
                    <Box sx={{ position: 'relative', width: '100%', height: '300px', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                        <CardMedia
                            component="img"
                            image={beautifulBmoreImage}
                            alt="Household"
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                minWidth: '100%',
                                minHeight: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Welcome to {householdNickname}
                        </Typography>
                        {propertyAddress && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 1,
                                    mb: 2,
                                    borderRadius: 1,
                                    bgcolor: 'grey.100',
                                    border: '1px solid',
                                    borderColor: 'grey.200'
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: '0.875rem', opacity: 0.8 }} aria-hidden>
                                  🏡
                                </Typography>
                                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                    {propertyAddress.replace(', USA', '')}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            View all the household bills, receipts, and due dates here.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Current number of roomies:{' '}
                            <Typography component="span" fontWeight="bold" fontSize="28px">
                                {roomiesCount}
                            </Typography>
                        </Typography>
                        <PrimaryButton fullWidth onClick={() => {router.visit(`/util/dashboard/${props.household_id}/roomies`)}}>
                            Add Roomie
                        </PrimaryButton>
                    </CardContent>
                </CustomCard>

                {/* Current cycle bills — replaces the old Past Due / Upcoming cards */}
                {(currentCycle.bills.length > 0 || currentCycle.unmatched_bills.length > 0) && (
                  <CustomCard>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <CurrentBillsCard
                            embedded
                            currentCycle={currentCycle}
                            householdId={props.household_id}
                            currentUserRoommateId={currentUserRoommateId}
                            expectedBills={expectedBills}
                            billPayerVenmoHandle={billPayerVenmoHandle}
                            isCustomer={props.is_customer}
                            currentRoommateIsAdmin={props.current_roommate_is_admin}
                            onCurrentCycleChange={setCurrentCycle}
                        />
                        <PrimaryButton fullWidth onClick={() => router.visit(`/util/dashboard/${props.household_id}/bills`)} sx={{ mt: 1 }}>
                            View All Bills
                        </PrimaryButton>
                    </CardContent>
                  </CustomCard>
                )}

                {/* Expected bills roster — landlord/admin setup, separate from the status card above */}
                {canManageRoster && (
                    <ExpectedBillsCard
                        householdId={props.household_id}
                        expectedBills={expectedBills}
                        onExpectedBillsChange={setExpectedBills}
                        isCustomer={props.is_customer}
                        currentRoommateIsAdmin={props.current_roommate_is_admin}
                    />
                )}

                {/* How to send bills */}
                <CustomCard>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Auto split bills easily
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Add the email below to your utility provider and we will split it and notify all roomies. 
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                bgcolor: 'grey.100',
                                border: '1px solid',
                                borderColor: 'grey.200'
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                                {householdEmail}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleCopyToClipboard(householdEmail, 'Household email')}
                                sx={{
                                    alignSelf: 'flex-start',
                                    color: 'black',
                                    borderColor: 'black',
                                    '&:hover': {
                                        color: 'black',
                                        borderColor: 'black',
                                        bgcolor: 'grey.200'
                                    },
                                    '&:active': {
                                        color: 'black',
                                        borderColor: 'black'
                                    }
                                }}
                            >
                                Copy
                            </Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Contact us if you need help with this. We got you covered.
                        </Typography>
                    </CardContent>
                </CustomCard>

                {/* Contact support */}
                <CustomCard>
                    <CardContent sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" mb={1} gap={1}>
                            <SupportIcon sx={{ color: 'grey.400' }} />
                            <Typography fontWeight="bold">Need Help?</Typography>
                        </Box>
                        <Typography variant="body2" mb={3}>
                            Contact our support team for assistance.
                        </Typography>
                        <SecondaryButton fullWidth onClick={handleOpenSupport}>
                            Contact Support
                        </SecondaryButton>
                    </CardContent>
                </CustomCard>

                {/* Support Dialog */}
                <Dialog
                    open={supportOpen}
                    onClose={handleCloseSupport}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Contact Support
                        </Typography>
                        <IconButton onClick={handleCloseSupport} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                                Text or email us for assistance:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', mb: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                        Email
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        rich@heyyproperty.com
                                    </Typography>
                                </Box>
                                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard('rich@heyyproperty.com', 'Email')} sx={{ minWidth: 'auto', px: 2 }}>
                                    Copy
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', mb: 3 }}>
                                <Box>
                                    <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                        Phone
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        617-459-2265
                                    </Typography>
                                </Box>
                                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard('617-459-2265', 'Phone number')} sx={{ minWidth: 'auto', px: 2 }}>
                                    Copy
                                </Button>
                            </Box>
                            {customerId && (
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: '8px', border: '1px solid', borderColor: 'grey.200' }}>
                                    <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                        Your Customer ID
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                            {customerId}
                                        </Typography>
                                        <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard(customerId, 'Customer ID')} sx={{ minWidth: 'auto', px: 2 }}>
                                            Copy
                                        </Button>
                                    </Box>
                                    <Typography variant="body2" color="grey.600" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                        When you contact support, give us your ID so we can find you in our system.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>

                <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </MainLayout>
        </>
    );
}
